<script setup lang="ts">
import { computed, ref } from 'vue';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { useServerStore, ConnectionState } from '@/src/store/server';

import {  useModelStore } from '@/src/store/datasets-models';
import { useModel } from '@/src/composables/useModel';

import { vec3 } from 'gl-matrix';
import { OBLIQUE_OUTLINE_COLORS, MODEL_ORGAN_COLORS } from '@/src/constants';
import type { RGBColor, Vector3 } from '@kitware/vtk.js/types';


const serverStore = useServerStore();
const { client } = serverStore;
const simiInitLoading = ref(false);
const ready = computed( async () => {
    serverStore.connState === ConnectionState.Connected;
    simiInitLoading.value = true;
    try {
      await client.call('initializeSim', []);
    } finally {
      simiInitLoading.value = false;
    }
  }
);


// const medianFilterLoading = ref(false);
const { currentImageID } = useCurrentImage();
// const medianFilterRadius = ref(2);


// --- generate Simulation --- //

const genSimuLoading = ref(false);
const simGenedReady = ref(false);
const generateSimulation = async () => {
  const id = currentImageID.value;
  if (!id) return;

  genSimuLoading.value = true;
  try {
    await client.call('generateSimulation', [id]);
  } finally {
    genSimuLoading.value = false;
    simGenedReady.value = true;
  }
};

const hasCurrentImage = computed(() => !!currentImageID.value);


const coucou = computed({
  get: () => 1,
  set: (id) => console.log(id),
});

const { idList, dataIndex, metadata } = useModelStore();

const modelProps = computed(() => {
  return idList.map((modelID) => {
    return {
      id: modelID,
      name: metadata[modelID].name,
      display: metadata[modelID].display,
      cbId: "cb-${metadata[modelID].name}",
    };
  });
});

const toggleVisibility = (id: string) =>
{
  const { polydata, metadata } = useModel(id);
  metadata.value.display = !metadata.value.display
};


// --- start/stop/reset Simulation --- //

const startSimuLoading = ref(false);
const startSimulation = async () => {
  const id = currentImageID.value;
  if (!id) return;

  console.log("we call START")

  startSimuLoading.value = true;
  try {
    await client.call('startSimulation', [id]);
  } finally {
    startSimuLoading.value = false;
  }
};

const stopSimuLoading = ref(false);
const stopSimulation = async () => {
  const id = currentImageID.value;
  if (!id) return;

  console.log("we call STOP")

  stopSimuLoading.value = true;
  try {
    await client.call('stopSimulation', [id]);
  } finally {
    stopSimuLoading.value = false;
  }
};

const resetSimuLoading = ref(false);
const resetSimulation = async () => {
  const id = currentImageID.value;
  if (!id) return;
  console.log("we call RESET")

  resetSimuLoading.value = true;
  try {
    await client.call('resetSimulation', [id]);
  } finally {
    resetSimuLoading.value = false;
  }
};

</script>

<template>
  <div class="overflow-y-auto overflow-x-hidden ma-2 fill-height">
    <v-alert v-if="!ready" color="info">Not connected to the server.</v-alert>

    <v-divider />

    <v-container>
      <v-row>
        <v-col>
          <v-list-header align="center">DIGYNE - Simulation</v-list-header>
        </v-col>
      </v-row>
    </v-container>

    <v-divider />

    <v-container>
      <v-row >
        <v-col>
          <v-btn @click="generateSimulation" :loading="genSimuLoading" :disabled="!ready">
            Generate simulation
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-divider />

    <v-list-subheader>3D Organs</v-list-subheader>
    <template v-for="(model, idx) in modelProps" :key="idx">
      <v-checkbox id="model.cbId" v-model="model.display"
        :label="`${model.name}`"
        density="comfortable"
        @change="toggleVisibility(model.id)"
        hide-details
      />
    </template>

    <v-divider />

    <v-container>
      <v-row >
        <v-col>
          <v-btn @click="startSimulation" :disabled="!simGenedReady">
            Start
          </v-btn>
        </v-col>
        <v-col>
          <v-btn @click="stopSimulation" :disabled="!simGenedReady">
            Pause
          </v-btn>
        </v-col>
        <v-col>
          <v-btn @click="resetSimulation" :disabled="!simGenedReady">
            Reset
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

  </div>
</template>
